import React, { useCallback, useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import * as api from "./csaApi";
import CsaAuditInsight from "./CsaAuditInsight";
import CsaQuestionTrend from "./CsaQuestionTrend";
import "./CsaDashboard.css";

/**
 * CSA trend — one site's audits compared over time (design §12 item 11).
 *
 * A third tab beside the Electrical and HSE dashboards, using the same
 * org → site selection those already establish. The difference is what happens
 * after you choose: they show one report, this shows the series.
 *
 * Three things the design pins down, and this screen holds to:
 *
 *   · The trend key is `(org_id, site_id)` — never the site name (decision 6).
 *     The selector passes ids straight back to the server.
 *
 *   · A null percentage is a **gap in the line**, not a zero. A pillar that was
 *     scoped out was not assessed, and drawing it at 0% would tell a client
 *     they failed something nobody looked at.
 *
 *   · A framework version change is marked on the chart. Pillar roll-ups still
 *     compare across one — they aggregate many questions — but a jump that
 *     lands exactly on a new version deserves a second look before anybody
 *     calls it improvement (§10c).
 *
 * The numbers are read, never computed here. Conformance comes from the frozen
 * snapshot the server wrote at submit; a second implementation on the client
 * would eventually disagree with the report.
 */

/**
 * Fallback only. The real palette comes from the framework — the server returns
 * `bands` with each band's configured colour — so restyling a band is a
 * configuration change, not a code change. This is what gets used if a band is
 * missing from that table, or on an older server that does not send it.
 */
const BAND_FALLBACK = {
  Optimised: "#2e7d4f",
  Established: "#4a8c81",
  Developing: "#b8860b",
  Initial: "#c0392b",
  "Not assessed": "#7c8a87",
};

export default function CsaDashboard() {
  const [sites, setSites] = useState(null);
  const [chosen, setChosen] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /*
   * "" means the trend across every audit at this site. Anything else is one
   * audit's id, and the single-audit dashboard replaces the trend.
   *
   * One selector rather than two tabs: the site is the same either way, and the
   * question "which audit" is a narrowing of "this site", not a different mode.
   */
  const [oneAudit, setOneAudit] = useState("");

  /* ---------------- which sites have a history ---------------- */

  useEffect(() => {
    let live = true;
    api
      .trendSites()
      .then((r) => {
        if (!live) return;
        const rows = (r && r.sites) || [];
        setSites(rows);
        // Land on something useful. The site with the most audits is the only
        // one that can show a trend at all.
        if (rows.length) setChosen(rows[0].org_id + ":" + rows[0].site_id);
      })
      .catch((e) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, []);

  /* ---------------- the series ---------------- */

  const load = useCallback((key) => {
    if (!key) return;
    const [orgId, siteId] = key.split(":");
    setLoading(true);
    setError(null);
    api
      .trend(orgId, siteId)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(chosen);
  }, [chosen, load]);

  /* ---------------- chart shapes ---------------- */

  /* Audits are the x axis. Labelled by the year of the visit, because that is
     how an auditor refers to them — "the 2024 audit" — falling back to the
     date when two land in the same year. */
  const labels = useMemo(() => {
    if (!data || !data.audits) return [];
    const years = data.audits.map((a) => String(a.start_date || "").slice(0, 4));
    const clash = new Set(years).size !== years.length;
    return data.audits.map((a, i) =>
      clash ? fmtDate(a.start_date) : years[i] || fmtDate(a.start_date)
    );
  }, [data]);

  const overallChart = useMemo(() => {
    if (!data || !data.audits || data.audits.length < 2) return null;
    return {
      series: [
        {
          name: "Overall conformance",
          data: data.overall.map((o) => (o.pct == null ? null : o.pct)),
        },
      ],
      options: baseOptions(labels, data.version_changes, "#307268"),
    };
  }, [data, labels]);

  const pillarChart = useMemo(() => {
    if (!data || !data.pillars || data.audits.length < 2) return null;
    return {
      series: data.pillars.map((p) => ({
        name: "P" + p.no + " " + shorten(p.name),
        // null survives all the way here on purpose — ApexCharts leaves a gap.
        data: p.points.map((pt) => (pt && pt.pct != null ? pt.pct : null)),
      })),
      options: baseOptions(labels, data.version_changes, null),
    };
  }, [data, labels]);

  /** The framework's row for a band — its colour and what it asserts. */
  const bandInfo = useCallback(
    (name) => {
      const row = ((data && data.bands) || []).find((b) => b.name === name);
      return {
        color: (row && row.color) || BAND_FALLBACK[name] || "#7c8a87",
        statement: (row && row.statement) || null,
        range: row ? `${row.min_pct}–${row.max_pct}%` : null,
      };
    },
    [data]
  );

  /* ---------------- render ---------------- */

  if (error) {
    return (
      <div className="csa-dash">
        <p className="cd-error">{error}</p>
      </div>
    );
  }

  if (sites === null) {
    return (
      <div className="csa-dash">
        <p className="cd-empty">Loading…</p>
      </div>
    );
  }

  if (!sites.length) {
    return (
      <div className="csa-dash">
        <div className="cd-empty-state">
          <h3>Nothing to compare yet</h3>
          <p>
            A trend needs finished audits. Once a CSA audit has been submitted,
            its site appears here; once a site has been audited twice, this shows
            what changed between the visits.
          </p>
        </div>
      </div>
    );
  }

  const single = data && data.audits && data.audits.length === 1;

  return (
    <div className="csa-dash">
      {/* ---------------- the selector ---------------- */}
      <div className="cd-head">
        <label htmlFor="cd-site">Site</label>
        <select
          id="cd-site"
          value={chosen}
          onChange={(e) => {
            setChosen(e.target.value);
            setOneAudit("");
          }}
        >
          {sites.map((s) => (
            <option key={s.org_id + ":" + s.site_id} value={s.org_id + ":" + s.site_id}>
              {s.org_name} — {s.site_name} ({s.audits} audit{s.audits === 1 ? "" : "s"})
            </option>
          ))}
        </select>

        {data && data.audits.length ? (
          <>
            <label htmlFor="cd-audit">Show</label>
            <select
              id="cd-audit"
              value={oneAudit}
              onChange={(e) => setOneAudit(e.target.value)}
            >
              <option value="">Trend across all {data.audits.length} audits</option>
              {/* Newest first here, the opposite of the chart. A reader picking
                  one audit almost always wants the latest; a reader reading a
                  trend wants to start at the beginning. */}
              {[...data.audits].reverse().map((a) => (
                <option key={a.audit_id} value={a.audit_id}>
                  {String(a.start_date || "").slice(0, 10)} —{" "}
                  {a.overall_pct == null ? "not scored" : a.overall_pct + "%"}
                </option>
              ))}
            </select>
          </>
        ) : null}

        {data && data.audits.length && !oneAudit ? (
          <span className="cd-latest">
            Latest{" "}
            <strong>
              {data.audits[data.audits.length - 1].overall_pct == null
                ? "—"
                : data.audits[data.audits.length - 1].overall_pct + "%"}
            </strong>
            <span
              className="cd-band"
              style={{
                background: bandInfo(data.audits[data.audits.length - 1].maturity_band).color,
              }}
              title={bandInfo(data.audits[data.audits.length - 1].maturity_band).statement || undefined}
            >
              {data.audits[data.audits.length - 1].maturity_band || "—"}
            </span>
          </span>
        ) : null}
      </div>

      {loading ? <p className="cd-empty">Loading the trend…</p> : null}

      {!loading && oneAudit ? (
        <CsaAuditInsight auditId={oneAudit} onBack={() => setOneAudit("")} />
      ) : null}

      {!loading && !oneAudit && data ? (
        <>
          <p className="cd-summary">{data.summary}</p>

          {single ? (
            <div className="cd-empty-state small">
              <h3>One audit so far</h3>
              <p>
                There is nothing to compare it against yet. The next audit at
                this site will show what moved.
              </p>
            </div>
          ) : null}

          {data.version_changes && data.version_changes.length ? (
            <div className="cd-warn">
              <strong>The framework changed during this history.</strong> Pillar
              scores still compare — they aggregate many questions — but a change
              at{" "}
              {data.version_changes
                .map((v) => labels[v.at_index] + " (" + v.from + " → " + v.to + ")")
                .join(", ")}{" "}
              may reflect a different set of questions rather than a different
              site.
            </div>
          ) : null}

          {/* ---------------- overall ---------------- */}
          {overallChart ? (
            <section className="cd-card">
              <h3>Overall conformance</h3>
              <Chart
                options={overallChart.options}
                series={overallChart.series}
                type="line"
                height={260}
              />
            </section>
          ) : null}

          {/* ---------------- what moved ----------------

              The table, not the chart, is what a reader takes away. Six
              overlapping lines are hard to read; "P1 up 4.8" is not. */}
          {data.movement && data.movement.length && !single ? (
            <section className="cd-card">
              <h3>What moved, first visit to last</h3>
              <table className="cd-move">
                <thead>
                  <tr>
                    <th>Pillar</th>
                    <th className="n">First</th>
                    <th className="n">Latest</th>
                    <th className="n">Change</th>
                  </tr>
                </thead>
                <tbody>
                  {data.movement.map((m) => (
                    <tr key={m.no}>
                      <td>
                        <span className="cd-pno">P{m.no}</span> {m.name}
                      </td>
                      <td className="n">{m.first == null ? "—" : m.first + "%"}</td>
                      <td className="n">{m.last == null ? "—" : m.last + "%"}</td>
                      <td className={"n " + deltaClass(m.delta)}>{deltaText(m.delta)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="cd-foot">
                A dash means the pillar was not assessed in that audit — scoped
                out, not scored zero.
              </p>
            </section>
          ) : null}

          {/* ---------------- per pillar ---------------- */}
          {pillarChart ? (
            <section className="cd-card">
              <h3>By pillar</h3>
              <Chart
                options={pillarChart.options}
                series={pillarChart.series}
                type="line"
                height={340}
              />
            </section>
          ) : null}

          {/* ---------------- the audits themselves ---------------- */}
          <section className="cd-card">
            <h3>The audits behind this</h3>
            <table className="cd-audits">
              <thead>
                <tr>
                  <th>Report</th>
                  <th>Visited</th>
                  <th>Submitted</th>
                  <th className="n">Conformance</th>
                  <th>Band</th>
                </tr>
              </thead>
              <tbody>
                {data.audits.map((a) => (
                  <tr key={a.audit_id}>
                    <td className="mono">{a.report_id}</td>
                    <td>{fmtDate(a.start_date)}</td>
                    <td>{fmtDate(a.submitted_at)}</td>
                    <td className="n">{a.overall_pct == null ? "—" : a.overall_pct + "%"}</td>
                    <td>
                      <span
                        className="cd-band sm"
                        style={{ background: bandInfo(a.maturity_band).color }}
                        title={bandInfo(a.maturity_band).statement || undefined}
                      >
                        {a.maturity_band || "—"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {/* The per-question series (§10c). Placed last on purpose: the
              roll-ups answer "how is this site doing", and this answers "what
              happened to that one thing", which is the question somebody asks
              second. */}
          <section className="cd-card">
            <CsaQuestionTrend
              orgId={chosen.split(":")[0]}
              siteId={chosen.split(":")[1]}
            />
          </section>
        </>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Shared chart options.
 *
 * `yaxis` is pinned to 0–100 rather than left to fit the data. Auto-fitting
 * makes a two-point move from 97.4% to 97.6% fill the whole panel and look like
 * a transformation, which is the single most misleading thing a conformance
 * chart can do.
 */
function baseOptions(labels, versionChanges, colour) {
  return {
    chart: {
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: "inherit",
    },
    colors: colour ? [colour] : undefined,
    stroke: { width: 2.5, curve: "straight" },
    markers: { size: 4 },
    dataLabels: { enabled: false },
    xaxis: { categories: labels },
    yaxis: {
      min: 0,
      max: 100,
      tickAmount: 5,
      labels: { formatter: (v) => Math.round(v) + "%" },
    },
    // A missing point breaks the line rather than joining across it.
    legend: { position: "bottom", fontSize: "12px" },
    tooltip: {
      y: { formatter: (v) => (v == null ? "not assessed" : v + "%") },
    },
    annotations: {
      xaxis: (versionChanges || []).map((v) => ({
        x: labels[v.at_index],
        borderColor: "#b8860b",
        label: {
          text: "new framework",
          style: { background: "#b8860b", color: "#fff", fontSize: "10px" },
        },
      })),
    },
  };
}

function deltaText(d) {
  if (d == null) return "—";
  if (d === 0) return "no change";
  return (d > 0 ? "+" : "") + d;
}

function deltaClass(d) {
  if (d == null || d === 0) return "flat";
  return d > 0 ? "up" : "down";
}

function shorten(name) {
  const s = String(name || "");
  return s.length > 22 ? s.slice(0, 20).trimEnd() + "…" : s;
}

function fmtDate(d) {
  if (!d) return "—";
  const s = String(d).slice(0, 10);
  const [y, m, day] = s.split("-");
  if (!y || !m || !day) return s;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${Number(day)} ${months[Number(m) - 1] || m} ${y}`;
}
